import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, RefreshCw } from "lucide-react";
import { formatApiError, listAudit, toastApiError } from "@/lib/api";

const ACTION_FAMILIES = [
  { value: "__all__", label: "All actions" },
  { value: "auth", label: "Auth (login/register/logout)" },
  { value: "admin", label: "Admin (invites/users/cloak)" },
  { value: "trade", label: "Trades" },
  { value: "analysis", label: "Analysis" },
  { value: "csrf", label: "CSRF rejects" },
];

const ACTION_STYLES = {
  "auth.login.success": "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  "auth.login.failure": "border-red-500/40 text-red-400 bg-red-500/10",
  "auth.login.locked": "border-red-500/40 text-red-400 bg-red-500/10",
  "auth.logout": "border-neutral-500/40 text-neutral-400 bg-neutral-500/10",
  "auth.register": "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  "auth.password_change": "border-amber-400/40 text-amber-300 bg-amber-400/10",
  "admin.invite.create": "border-[var(--aurion-gold)]/40 text-[var(--aurion-gold)] bg-[var(--aurion-gold)]/10",
  "admin.invite.revoke": "border-red-500/40 text-red-400 bg-red-500/10",
  "admin.invite.used": "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  "admin.user.activate": "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  "admin.user.deactivate": "border-red-500/40 text-red-400 bg-red-500/10",
  "admin.cloak.open": "border-red-500/40 text-red-300 bg-red-500/10",
  "trade.execute": "border-[var(--aurion-gold)]/40 text-[var(--aurion-gold)] bg-[var(--aurion-gold)]/10",
  "analysis.run.start": "border-blue-400/40 text-blue-300 bg-blue-400/10",
  "csrf.reject": "border-red-500/40 text-red-400 bg-red-500/10",
};

export default function AuditPage() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [family, setFamily] = useState("__all__");
  const [userQuery, setUserQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (family && family !== "__all__") params.action = family;
      if (userQuery) params.user_id = userQuery.trim();
      const r = await listAudit(params);
      setItems(r.items);
      setTotal(r.total);
    } catch (e) {
      toastApiError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [family]);

  return (
    <div data-testid="audit-page">
      <div className="mb-6 flex items-baseline justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--aurion-muted)]">
            Master console · Audit
          </div>
          <div className="mt-1 text-2xl text-[var(--aurion-text)]">
            {items === null ? "…" : `${total} audit event${total === 1 ? "" : "s"}`}
          </div>
        </div>
        <Button
          data-testid="audit-refresh"
          onClick={load}
          variant="ghost"
          className="text-[var(--aurion-muted)] hover:bg-transparent hover:text-[var(--aurion-gold)]"
          disabled={loading}
        >
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      <Card className="mb-6 border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div>
            <div className="mb-1 text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
              Action family
            </div>
            <Select value={family} onValueChange={setFamily}>
              <SelectTrigger
                data-testid="audit-filter-family"
                className="h-10 border-[var(--aurion-border)] bg-[var(--aurion-panel-2)]"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-[var(--aurion-border)] bg-[var(--aurion-panel)] text-[var(--aurion-text)]">
                {ACTION_FAMILIES.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <div className="mb-1 text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
              User id (paste from Users tab)
            </div>
            <div className="flex gap-2">
              <Input
                data-testid="audit-filter-user"
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") load();
                }}
                placeholder="e.g. 37d00591-2d5a-…"
                className="h-10 border-[var(--aurion-border)] bg-[var(--aurion-panel-2)] font-mono"
              />
              <Button
                data-testid="audit-apply"
                onClick={load}
                className="h-10 border border-[var(--aurion-gold)] bg-[var(--aurion-gold)]/10 text-[var(--aurion-gold)] hover:bg-[var(--aurion-gold)]/20"
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Card className="border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-2">
        <Table>
          <TableHeader>
            <TableRow className="border-[var(--aurion-border)] hover:bg-transparent">
              <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                Time
              </TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                Action
              </TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                Actor
              </TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                IP
              </TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                Meta
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="audit-tbody">
            {items === null ? (
              <TableRow>
                <TableCell colSpan={5} className="p-6 text-center text-sm text-[var(--aurion-muted)]">
                  Loading…
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="p-6 text-center text-sm text-[var(--aurion-muted)]">
                  No events for this filter.
                </TableCell>
              </TableRow>
            ) : (
              items.map((it) => (
                <TableRow
                  key={it.id}
                  className="border-[var(--aurion-border)] hover:bg-[var(--aurion-panel-2)]"
                  data-testid={`audit-row-${it.action}`}
                >
                  <TableCell className="text-[11px] font-mono text-[var(--aurion-muted)]">
                    {new Date(it.timestamp).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`rounded-none text-[10px] uppercase tracking-widest ${
                        ACTION_STYLES[it.action] ||
                        "border-neutral-500/40 text-neutral-400 bg-neutral-500/10"
                      }`}
                    >
                      {it.action}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-[11px] text-[var(--aurion-muted)]">
                    {it.user ? (
                      <>
                        <div className="text-[var(--aurion-text)]">{it.user.email}</div>
                        <div className="text-[10px]">{it.user.name}</div>
                      </>
                    ) : it.user_id ? (
                      <span className="font-mono">{it.user_id.slice(0, 8)}…</span>
                    ) : (
                      <span className="italic">(unauthenticated)</span>
                    )}
                  </TableCell>
                  <TableCell className="text-[11px] font-mono text-[var(--aurion-muted)]">
                    {it.ip || "—"}
                  </TableCell>
                  <TableCell className="max-w-md text-[10px] font-mono text-[var(--aurion-muted)]">
                    {it.meta && Object.keys(it.meta).length
                      ? JSON.stringify(it.meta)
                      : "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
